import { HostListener, inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { environment } from '../../environments/environment';
import { BehaviorSubject, firstValueFrom, from, Observable, of, Subject, throwError } from 'rxjs';
import { map, catchError, switchMap, filter } from 'rxjs/operators';
import { HttpClient, HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpResponse, HttpErrorResponse, HttpEventType, HttpParams } from '@angular/common/http';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import * as base64 from 'base64-arraybuffer';
import { base64url } from 'jose';
import { AbstractControl, ValidationErrors } from '@angular/forms';
import { AuthServiceUrlProvider, ServiceRegion, AuthResponse } from 'cdss-common-lib';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService implements OnDestroy, AuthServiceUrlProvider {
  private token: string = null;
  private _uid: string = null;
  public usesTwoFactor: boolean;
  public publicKey: string;
  public privateKey: string;

  // Whether the user uses OAuth or SAML
  public usesOauth = false;

  private expiry: Date;
  private _roles: string[];

  private _scopeId?: string;
  private _scopeLevel?: "global" | "tenant";

  private _twoFactorStateChanged = new Subject<boolean>();
  private _isAuthenticated = new BehaviorSubject<boolean>(false);
  private frontendConfig: Promise<FrontendConfig>;

  private claims?: Promise<Set<string>>;
  private region?: ServiceRegion;

  constructor(private http: HttpClient, private router: Router, private ngZone: NgZone) {
    // There's a dependency loop between AuthenticationService and AuthenticationInterceptor
    // We break it by doing the HTTP request in a promise of its own, i.e. it gets run after this constructor.
    this.frontendConfig = new Promise<FrontendConfig>((resolve, reject) => {
      window.setTimeout(() => {
        this.http.get<FrontendConfig>('/config.json').subscribe(res => {
          if (res.features && res.features.enableSubtleDemo === undefined) {
            res.features.enableSubtleDemo = true; // true per default
          }
          resolve(res);
        }, err => reject(err));
      }, 0);
    });
  }

  getAuthServiceUrl(): Promise<string> {
    return this.getFrontendConfig().then(config => config.authServiceUrl);
  }

  ngOnDestroy(): void {
    this.stopTokenRefreshTimer();
  }

  public isAuthenticated(): boolean {
    return this.token !== null;
  }
  public canRefreshToken(): boolean {
    return localStorage.getItem("refreshToken") !== null;
  }

  public isAuthenticatedObservable() {
    return this._isAuthenticated.asObservable();
  }

  public getFrontendConfig() {
    return this.frontendConfig;
  }

  public getToken(): string {
    return this.token;
  }
  public getRefreshToken(): string {
    return localStorage.getItem("refreshToken");
  }

  private saveToken(token: string, rToken: string): void {
    this.token = token;
    // save the refresh token in the local browser storage so that we can use it after a browser restart / refresh / new tab to stay logged in
    localStorage.setItem("refreshToken", rToken);

    this.claims = this.frontendConfig.then(frontendConfig => {
      return firstValueFrom(
        this.http.get<string[]>(frontendConfig.authServiceUrl + '/api/v1/user/claims').pipe(map(claims => new Set(claims ?? [])))
      );
    });

    this._isAuthenticated.next(true);
    
    try {
      let decoded = this.decodeJwtContentsInsecure(this.token);

      if (decoded.exp)
        this.expiry = new Date(decoded.exp * 1000);
      else
        this.expiry = new Date(new Date().getTime() + 1000*60*10);

      this._scopeId = decoded.sid;
      this._scopeLevel = decoded.sl;

      this.usesOauth = decoded['oauth'] as boolean;
    } catch(e) {
      console.error(e);
      this.usesOauth = false;
    }
  }

  public forgetToken(): void {
    this.token = null;
    this.stopTokenRefreshTimer();
    localStorage.removeItem("refreshToken");
  }

  public async hasOneOfClaims(...claim: string[]): Promise<boolean> {
    const claims = (await this.claims);
    if (!claims)
      return false;

    return claim.some(c => claims.has(c));
  }

  public async getCanUseClaims(): Promise<string[]> {
    const claims = (await this.claims);
    return Array.from(claims).filter(c => c.startsWith('canUse'));
  }

  private processAuthenticationResponse(response: AuthResponse) {
    this.saveToken(response.token, response.refreshToken);

    this._uid = response.uid;
    this.publicKey = response.publicKey;
    this.privateKey = response.privateKey;
    this.usesTwoFactor = response.uses2FA;
    this._roles = response.roles;
    this.region = response.region;
  }

  public get twoFactorStateChanged() {
    return this._twoFactorStateChanged;
  }

  public getServiceRegion(): ServiceRegion | undefined {
    return this.region;
  }

  public get roles(): string[] {
    return this._roles;
  }

  public hasRole(role: string): boolean {
    if (!this.isAuthenticated())
      return false;
      
    if (this._roles.includes(role))
      return true;
    if (this._roles.includes('root'))
      return true;
    return false;
  }

  public getBackendConfiguration(): Observable<AuthBackendConfiguration> {
    return from(this.frontendConfig).pipe(switchMap(config => {
      return this.http.get<AuthBackendConfiguration>(config.authServiceUrl + '/api/v1/config');
    }));
  }

  public authenticate(email: string, password: string, twoFactorCode?: string) : Observable<AuthenticationResult> {
    let headers;

    if (twoFactorCode)
      headers = { 'X-2FA-Code': twoFactorCode };

    return from(this.frontendConfig).pipe(switchMap(config => {
      return this.http.post(config.authServiceUrl + '/api/v1/user/login', {
        email: email,
        password: password
      }, {
        observe: 'response',
        headers: headers
      }).pipe(map(response => {
        this.processAuthenticationResponse(response.body as AuthResponse);
        this.startTokenRefreshTimer();

        return { uid: this.uid, success: true };
      }), catchError((response: HttpErrorResponse) => {
        if (response.status === 401) {
          return of({ twoFactorNeeded: true, success: false });
        }

        let errorMessage = response.message;
        if (typeof response.error === 'object' && response.error.message)
          errorMessage = response.error.message;

        return of({ success: false, message: errorMessage });
      }));
    }));
  }

  public exchangeCodeForToken(code: string, twoFactorCode?: string) : Observable<AuthenticationResult> {
    let headers;

    if (twoFactorCode)
      headers = { 'X-2FA-Code': twoFactorCode };

    return from(this.frontendConfig).pipe(switchMap(config => {
      return this.http.post(config.authServiceUrl + '/api/v1/user/login/oauth/token', { code: code }, {
        observe: 'response',
        headers: headers
      }).pipe(map(response => {
        this.processAuthenticationResponse(response.body as AuthResponse);

        this.startTokenRefreshTimer();

        return { uid: this.uid, success: true };
      }), catchError(response => {
        if (response.status === 401) {
          return of({ twoFactorNeeded: true, success: false });
        }

        return of({ success: false });
      }));
    }));
}

  get uid(): string {
    return this._uid;
  }

  @HostListener('window:focus', ['$event'])
  private onWindowFocused() {
    if (this.token && this.expiry) {
      let now = new Date();
      if (now.getTime() > this.expiry.getTime())
        this.refreshToken();
    }
  }

  private refreshToken() {
    return from(this.frontendConfig).subscribe(config => {
      this.http.post<object>(config.authServiceUrl + '/api/v1/user/refreshToken', '')
      .pipe(catchError(err => of(null)))
      .subscribe(obj => {
        if (obj) {
          this.processAuthenticationResponse(obj as AuthResponse);
          this.startTokenRefreshTimer();
        } else {
          localStorage.removeItem('refreshToken');
          this.router.navigateByUrl('/login');
          this.token = null;
          this.stopTokenRefreshTimer();
        }
      });
    });
  }

  public reviveToken(): Observable<AuthenticationResult> {
    if (!this.canRefreshToken())
      return of({ success: false });

    console.log('adopting refresh token from local storage');
    return this.adoptToken();
  }

  // Takes an externally provided refresh token and attempts to do a refresh
  public adoptToken() : Observable<AuthenticationResult> {
    this.stopTokenRefreshTimer();

    return from(this.frontendConfig).pipe(switchMap(config => {
      return this.http.post<object>(config.authServiceUrl + '/api/v1/user/refreshToken', '')
      .pipe(map(obj => {
          this.processAuthenticationResponse(obj as AuthResponse);
          this.startTokenRefreshTimer();

          let res = new AuthenticationResult();
          res.success = true;
          res.twoFactorNeeded = obj['uses2FA'];
          res.uid = obj['uid'];

          return res;
      }), catchError(err => {
        console.log('adoptToken failed', err);

        return of({ success: false });
      }));
    }));
  }

  public getProfile(uid: string) : Observable<UserProfile> {
    return from(this.frontendConfig).pipe(switchMap(config => {
      return this.http.get<UserProfile>(config.authServiceUrl + '/api/v1/user/' + uid);
    }));
  }

  public modifyProfile(uid: string, profile: UserProfile, twoFactorCode?: string) : Observable<HttpResponse<void>> {
    let headers;

    if (twoFactorCode)
      headers = { 'X-2FA-Code': twoFactorCode };

    return from(this.frontendConfig).pipe(switchMap(config => {
      return this.http.put<void>(config.authServiceUrl + '/api/v1/user/' + uid, profile, { observe: 'response', headers: headers });
    }));
  }

  public get2FAState(uid: string): Observable<boolean> {
    return from(this.frontendConfig).pipe(switchMap(config => {
      return this.http.get(config.authServiceUrl + '/api/v1/user/' + uid + '/2fa').pipe(map(res => res['enabled']));
    }));
  }

  // Returns the new secret if enabled is true
  // twoFactorCode only needed when disabling 2FA
  public set2FAState(uid: string, enabled: boolean, twoFactorCode?: string) : Observable<string | boolean> {
    let headers;

    if (twoFactorCode)
      headers = { 'X-2FA-Code': twoFactorCode };

    return from(this.frontendConfig).pipe(switchMap(config => {
      return this.http.put(config.authServiceUrl + '/api/v1/user/' + uid + '/2fa',
        { enabled },
        { headers: headers })
      .pipe(map(res => {
        if (!res['enabled'])
          this._twoFactorStateChanged.next(false);
          
        return res['secret'] || res['enabled'];
      }));
    }));
  }

  // To be called after set2FAState() with enabled = true
  public confirm2FAState(uid: string, otpCode: string) : Observable<TFARecoveryCodes> {
    return from(this.frontendConfig).pipe(switchMap(config => {
      return this.http.post<TFARecoveryCodes>(config.authServiceUrl + '/api/v1/user/' + uid + '/2fa', {},
        { headers: { 'X-2FA-Code': otpCode } }).pipe(map(res => {

          this._twoFactorStateChanged.next(true);
          return res;
        }));
    }));
  }

  public getInvoicingSettings(uid: string) : Observable<InvoicingSettings> {
    return from(this.frontendConfig).pipe(switchMap(config => {
      return this.http.get<InvoicingSettings>(config.authServiceUrl + '/api/v1/user/' + uid + '/invoicing');
    }));
  }

  public setInvoicingSettings(uid: string, settings: InvoicingSettings) : Observable<HttpResponse<void>> {
    return from(this.frontendConfig).pipe(switchMap(config => {
      return this.http.put<void>(config.authServiceUrl + '/api/v1/user/' + uid + '/invoicing', settings, { observe: 'response' });
    }));
  }

  public getReportDetailsProfile(uid: string): Observable<InstitutionProfile> {
    return from(this.frontendConfig).pipe(switchMap(config => {
      return this.http.get<object>(config.authServiceUrl + `/api/v1/user/${uid}/reportDetails`).pipe(map(data => {
        let logoData = data['institutionLogo'];
        return {
          institutionName: data['institutionName'],
          institutionLogo: logoData ? base64.decode(logoData) : null,
        };
      }));
    }));
  }

  public setReportDetailsProfile(uid: string, profile: InstitutionProfile): Observable<void> {
    let logoEncoded;

    if (profile.institutionLogo)
      logoEncoded = base64.encode(profile.institutionLogo);

    let req = {
      institutionName: profile.institutionName,
      institutionLogo: logoEncoded,
    };

    return from(this.frontendConfig).pipe(switchMap(config => {
      return this.http.put<void>(config.authServiceUrl + `/api/v1/user/${uid}/reportDetails`, req);
    }));
  }

  public getSocialLoginProviders(): Observable<SocialLoginProvider[]> {
    return from(this.frontendConfig).pipe(switchMap(config => {
      return this.http.get<SocialLoginProvider[]>(config.authServiceUrl + '/api/v1/user/login/providers');
    }));
  }

  public registerUserFromInvitation(invitationId: string, invitationKey: string, password: string): Observable<void> {
    const data = {
      key: invitationKey,
      password,
    };

    return from(this.frontendConfig).pipe(switchMap(config => {
      return this.http.post<object>(config.authServiceUrl + `/api/v1/user/invite/${invitationId}/use`, data).pipe(map(resp => {
        this.startTokenRefreshTimer();
        this.processAuthenticationResponse(resp as AuthResponse);
        return null;
      }))
    }));
  }

  public registerUser(email: string, password: string): Observable<void> {
    let request = {
      email: email,
      password: password,
    }

    return from(this.frontendConfig).pipe(switchMap(config => {
      return this.http.post<void>(config.authServiceUrl + '/api/v1/user', request);
    }));
  }

  public requestAccountDeletion(): Observable<void> {
    return from(this.frontendConfig).pipe(switchMap(config => {
      return this.http.post<void>(config.authServiceUrl + '/api/v1/user/' + this.uid + '/requestDeletion', '');
    }));
  }

  public requestForgottenPasswordEmail(email: string): Observable<void> {
    let body = {
      email,
    };

    return from(this.frontendConfig).pipe(switchMap(config => {
      return this.http.post<void>(config.authServiceUrl + '/api/v1/user/forgottenPassword', body);
    }));
  }

  public setForgottenPassword(uid: string, recoveryKey: string, newPassword: string) {
    let req = {
      password: newPassword,
      recoveryKey,
    };

    return from(this.frontendConfig).pipe(switchMap(config => {
      return this.http.put(config.authServiceUrl + `/api/v1/user/${uid}/setForgottenPassword`, req);
    }));
  }

  public getAccountDeletionDetails(uid: string, code: string) {
    let params = new HttpParams().append('code', code);
    return from(this.frontendConfig).pipe(switchMap(config => {
      return this.http.get<AccountDeletionDetails>(config.authServiceUrl + `/api/v1/user/${uid}/approveDeletion`, { params });
    }));
  }

  public cancelDeletion(uid: string, code: string) {
    return from(this.frontendConfig).pipe(switchMap(config => {
      return this.http.post(config.authServiceUrl + `/api/v1/user/${uid}/cancelDeletion`, { code });
    }));
  }

  // User must be authenticated!
  public approveDeletion(code: string) {
    if (!this._uid)
      return throwError(new Error("Not logged in"));
    
    return from(this.frontendConfig).pipe(switchMap(config => {
      return this.http.post(config.authServiceUrl + `/api/v1/user/${this._uid}/approveDeletion`, { code });
    }));
  }

  private refreshTimer: number = 0;
  private stopTokenRefreshTimer() {
    if (this.refreshTimer) {
      window.clearTimeout(this.refreshTimer);
      this.refreshTimer = 0;
    }
  }
  private startTokenRefreshTimer() {
    this.stopTokenRefreshTimer();

    let refreshDelay = 1000*60*10;
    // do we have a token already?
    try {
      // YES --> take care that we refresh before it expires (after 2/3 of the time left)
      let decoded = this.decodeJwtContentsInsecure(this.token);
      if (decoded.exp) {
        let diff = (decoded.exp - new Date().getTime() / 1000);
        if (diff >= 0) {
          refreshDelay = (diff / 3 * 2) * 1000;
        }
      }
    } catch (e) {
      console.log("Could not decode JWT contents, using default refresh delay", e);
    }

    // Why this is needed: https://www.protractortest.org/#/timeouts
    // Without this, E2E tests will think that the page is still loading due to the timer below.
    this.ngZone.runOutsideAngular(() => {
      this.refreshTimer = window.setTimeout(() => {
        this.refreshTimer = 0;
        this.ngZone.run(() => this.refreshToken());
      }, refreshDelay);
    });
  }

  private decodeJwtContentsInsecure(jwt: string): any {
    const textDecoder = new TextDecoder();

    return JSON.parse(textDecoder.decode(base64url.decode(jwt.split('.')[1])))
  }

  public get scopeLevel() {
    return this._scopeLevel;
  }

  public get scopeId() {
    return this._scopeId;
  }

  public continueAuthentication(email: string): Observable<AuthType | null> {
    return from(this.frontendConfig).pipe(switchMap(config => {
      return this.http.post<AuthType>(config.authServiceUrl + '/api/v1/user/login/continue', { email })
        .pipe(catchError(err => {
          if (err instanceof HttpErrorResponse && err.status === 404)
            return of(null);
          throw err;
        }));
    }));
  }
}

export interface SocialLoginProvider {
  id: string;
  name: string;
  bgcolor: string;
  icon: string;
  url: string;
}

export class AuthenticationResult {
  uid?: string;
  success: boolean;
  twoFactorNeeded?: boolean;
  message?: string;
}

export interface UserProfile {
  email?: string;
  affiliation?: string;
  name?: string;
  password?: string; // Only towards the server
  phone?: string;
}

export interface UserObject extends UserProfile {
  id: string;
  oauthProvider?: string;
  created: Date;
  priceList?: string;
  companyName?: string;
  uses2FA?: boolean;
  roles?: string;

  scopeLevel?: "global" | "tenant";
  scopeId?: string;

  groups: UserGroupMembership[];
}

export interface UserGroupMembership {
  id: string;
  name: string;
}

export class InvoicingSettings {
  companyName?: string;
  companyID?: string;
  vatID?: string;
  addressLine1?: string;
	addressLine2?: string;
	city?: string;
	zip?: string;
	state?: string;
	countryCode?: string;
  billingPeriod?: "monthly" | "quarterly" | "yearly";
}

export interface InstitutionProfile {
  institutionName?: string;
  institutionLogo?: ArrayBuffer;
}

export interface AuthBackendConfiguration {
  accountApprovalNeeded: boolean;
}

export interface AccountDeletionDetails {
  oauth?: string;
  '2fa'?: boolean;
}

export interface TFARecoveryCodes {
  recoveryCodes: string[];
}

export interface FrontendConfig {
  authServiceUrl: string;
  productionMode?: boolean;
  features?: FeaturesConfig;
  globalVersion?: string;
}

export interface FeaturesConfig {
  enableRadiomics?: boolean;
  enableCdss?: boolean;
  enableAutoml?: boolean;
  enableSubtleDemo?: boolean;
  enableCdssCommit?: boolean;
}

const regionalServicePrefixes = [
  '/api/v1/cdss',
  '/api/v1/automl-submission',
  '/api/v1/dicom-node',
  '/api/v1/file-upload',
  '/api/v1/generic-job',
  '/api/v1/radiomics',
  '/texlive',
];

@Injectable({
  providedIn: 'root'
})
export class AuthenticationInterceptor implements HttpInterceptor {
  private explicitRegion?: string;

  constructor(private authenticationService: AuthenticationService, private router: Router, private activatedRoute: ActivatedRoute) {

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        const params = this.getRouteParams(activatedRoute);
        this.explicitRegion = params.region;
      });
  }


  private getRouteParams(route: ActivatedRoute): any {
    let currentRoute = route;
    while (currentRoute.firstChild) {
      currentRoute = currentRoute.firstChild;
    }
    return currentRoute.snapshot.params;
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.authenticationService.canRefreshToken()) {
      req = req.clone({
        setHeaders: {
          "Authorization": "Bearer " + (req.url.endsWith("/refreshToken") ? this.authenticationService.getRefreshToken() : this.authenticationService.getToken())
        }
      });
    }
    
    if (this.isRegionalPath(req)) {
      const region = this.authenticationService.getServiceRegion();
      let regionHostname = this.explicitRegion || region?.hostname;
      if (regionHostname) {
        let url = req.url;
        
        if (req.url.startsWith('https://'))
          url = req.url.replace(/https?:\/\/[^/]+/, `https://${regionHostname}`);
        else if (req.url.startsWith('/'))
          url = `https://${regionHostname}${req.url}`;

        req = req.clone({
          url: url
        });
      }
    }

    // CSF-336
    window['dedicaidTestInterceptor']?.(req);

    return next.handle(req).pipe(map(event => {
      if (event.type === HttpEventType.ResponseHeader) {
        if (!req.url.endsWith("/refreshToken") && !req.url.endsWith("/user/login")) {
          if (event.status === 401)
            this.router.navigateByUrl("/login");
        }
      }
      return event;
    }));
  }

  private isRegionalPath(req: HttpRequest<any>): boolean {

    if (regionalServicePrefixes.some(prefix => req.url.startsWith(prefix))) {
      return true;
    }

    return false;
  }
}

export function passwordQualityCheck(control: AbstractControl<string>): ValidationErrors | null {
  const password = control.value;

  if (password.length < 8)
    return { "minlength": true };

  if (!/[a-z]/.test(password) || !/[A-Z]/.test(password) || !/[0-9]/.test(password))
    return { "complexity": true };
  
  let foundSymbol = false;
  for (let i = 0; i < password.length; i++) {
    const code = password.charCodeAt(i);

    // https://owasp.org/www-community/password-special-characters
    if ((code >= 0x20 && code <= 0x2f) || (code >= 0x3a && code <= 0x40) || (code >= 0x5b && code <= 0x60) || (code >= 0x7b && code <= 0x7e)) {
      foundSymbol = true;
      break;
    }
  }

  if (!foundSymbol)
    return { "complexity": true };

  return null;
}

export interface AuthType {
  continue: string;
}

export function determineTexliveEndpoint(): string {
  const authenticationService = inject(AuthenticationService);
  const url = window.location.href;
  // Sharing links include region hostname as the last segment
  if (url.includes('/sharing/')) {
    return 'https://' + url.split('/').pop() + '/texlive/';
  }

  const region = authenticationService.getServiceRegion();
  if (region?.hostname) {
    return `https://${region.hostname}/texlive/`;
  }
  return '/texlive/';
}
